"use strict";
// Pure city probe geometry shared by the website and senior MCP.
const { hasValidCoordinates } = require("./locationSearchMode");
const HELPER_CITY_GRID_SEARCH_OFFSET_MILES = 5;
const HELPER_CITY_GRID_SEARCH_MAX_PROBES = 12;
const MILES_PER_LATITUDE_DEGREE = 69;
const EARTH_RADIUS_MILES = 3958.8;
const CITY_GRID_SEARCH_DIRECTIONS = Object.freeze([
  { key: "north", north: 1, east: 0 },
  { key: "east", north: 0, east: 1 },
  { key: "south", north: -1, east: 0 },
  { key: "west", north: 0, east: -1 },
  { key: "northeast", north: Math.SQRT1_2, east: Math.SQRT1_2 },
  { key: "southeast", north: -Math.SQRT1_2, east: Math.SQRT1_2 },
  { key: "northwest", north: Math.SQRT1_2, east: -Math.SQRT1_2 },
  { key: "southwest", north: -Math.SQRT1_2, east: -Math.SQRT1_2 },
]);
const buildCityGridSearchCoordinates = ({
  latitude,
  longitude,
  offsetMiles = HELPER_CITY_GRID_SEARCH_OFFSET_MILES,
} = {}) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  const radiusMiles = Number(offsetMiles || 0);

  if (
    !hasValidCoordinates(lat, lng) ||
    !Number.isFinite(radiusMiles) ||
    radiusMiles <= 0
  ) {
    return [];
  }

  return CITY_GRID_SEARCH_DIRECTIONS.map((direction) => {
    const northMiles = radiusMiles * direction.north;
    const eastMiles = radiusMiles * direction.east;
    const nextLat = lat + northMiles / MILES_PER_LATITUDE_DEGREE;
    const milesPerLongitudeDegree = Math.max(
      1,
      MILES_PER_LATITUDE_DEGREE * Math.cos((lat * Math.PI) / 180),
    );
    const nextLng = lng + eastMiles / milesPerLongitudeDegree;

    return {
      key: direction.key,
      latitude: nextLat,
      longitude: nextLng,
    };
  });
};

const getFiniteCoordinateValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const getLatLngLiteral = (value) => {
  if (!value) return null;

  const lat =
    typeof value.lat === "function"
      ? value.lat()
      : (value.lat ?? value.latitude);
  const lng =
    typeof value.lng === "function"
      ? value.lng()
      : (value.lng ?? value.longitude);
  const latitude = getFiniteCoordinateValue(lat);
  const longitude = getFiniteCoordinateValue(lng);

  if (latitude === null || longitude === null) return null;
  return { lat: latitude, lng: longitude };
};

const getViewportCorner = (viewport, corner) => {
  const getterName = corner === "northeast" ? "getNorthEast" : "getSouthWest";
  if (typeof viewport?.[getterName] === "function") {
    return getLatLngLiteral(viewport[getterName]());
  }

  return getLatLngLiteral(
    viewport?.[corner] ||
      viewport?.[corner === "northeast" ? "northEast" : "southWest"] ||
      viewport?.[corner === "northeast" ? "ne" : "sw"],
  );
};

const normalizeLocationViewportBounds = (viewportOrBounds = null) => {
  const candidate =
    viewportOrBounds?.bounds ||
    viewportOrBounds?.viewport ||
    viewportOrBounds?.geometry?.viewport ||
    viewportOrBounds;
  if (!candidate || typeof candidate !== "object") return null;

  const north =
    getFiniteCoordinateValue(candidate.north) ??
    getViewportCorner(candidate, "northeast")?.lat;
  const east =
    getFiniteCoordinateValue(candidate.east) ??
    getViewportCorner(candidate, "northeast")?.lng;
  const south =
    getFiniteCoordinateValue(candidate.south) ??
    getViewportCorner(candidate, "southwest")?.lat;
  const west =
    getFiniteCoordinateValue(candidate.west) ??
    getViewportCorner(candidate, "southwest")?.lng;

  if (
    north === null ||
    east === null ||
    south === null ||
    west === null ||
    north <= south ||
    east === west
  ) {
    return null;
  }

  return { north, east, south, west };
};

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const getDistanceMiles = (left, right) => {
  const lat1 = toRadians(left.lat);
  const lat2 = toRadians(right.lat);
  const deltaLat = toRadians(right.lat - left.lat);
  const deltaLng = toRadians(right.lng - left.lng);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;

  return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const getViewportDimensionsMiles = (bounds) => {
  if (!bounds) {
    return { widthMiles: 0, heightMiles: 0, diagonalMiles: 0 };
  }

  const centerLat = (bounds.north + bounds.south) / 2;
  const centerLng = (bounds.east + bounds.west) / 2;
  const widthMiles = getDistanceMiles(
    { lat: centerLat, lng: bounds.west },
    { lat: centerLat, lng: bounds.east },
  );
  const heightMiles = getDistanceMiles(
    { lat: bounds.south, lng: centerLng },
    { lat: bounds.north, lng: centerLng },
  );
  const diagonalMiles = getDistanceMiles(
    { lat: bounds.south, lng: bounds.west },
    { lat: bounds.north, lng: bounds.east },
  );

  return { widthMiles, heightMiles, diagonalMiles };
};

const getViewportGridShape = ({ widthMiles, heightMiles, diagonalMiles }) => {
  if (diagonalMiles < 8) return { rows: 2, columns: 2 };
  if (diagonalMiles < 25) return { rows: 3, columns: 3 };
  if (widthMiles >= heightMiles * 1.2) return { rows: 3, columns: 4 };
  return { rows: 4, columns: 3 };
};

const isCoordinateInsideViewport = ({ latitude, longitude, bounds }) =>
  latitude >= bounds.south &&
  latitude <= bounds.north &&
  longitude >= bounds.west &&
  longitude <= bounds.east;

const buildCityViewportGridSearchCoordinates = ({
  latitude,
  longitude,
  viewport,
  maxProbes = HELPER_CITY_GRID_SEARCH_MAX_PROBES,
} = {}) => {
  const lat = Number(latitude);
  const lng = Number(longitude);
  const bounds = normalizeLocationViewportBounds(viewport);
  const probeLimit = Math.max(
    0,
    Math.floor(Number(maxProbes || HELPER_CITY_GRID_SEARCH_MAX_PROBES)),
  );

  if (!hasValidCoordinates(lat, lng) || !bounds || probeLimit <= 0) {
    return [];
  }

  if (!isCoordinateInsideViewport({ latitude: lat, longitude: lng, bounds })) {
    return [];
  }

  const dimensions = getViewportDimensionsMiles(bounds);
  if (dimensions.diagonalMiles <= 0) return [];

  const { rows, columns } = getViewportGridShape(dimensions);
  const minDistanceFromCenter = dimensions.diagonalMiles < 8 ? 0.1 : 0.75;
  const center = { lat, lng };
  const coordinates = [];

  for (let row = 1; row <= rows; row += 1) {
    const rowFraction = row / (rows + 1);
    const probeLat = bounds.south + (bounds.north - bounds.south) * rowFraction;

    for (let column = 1; column <= columns; column += 1) {
      const columnFraction = column / (columns + 1);
      const probeLng =
        bounds.west + (bounds.east - bounds.west) * columnFraction;
      const distanceFromCenter = getDistanceMiles(center, {
        lat: probeLat,
        lng: probeLng,
      });

      if (distanceFromCenter < minDistanceFromCenter) continue;

      coordinates.push({
        key: `viewport-r${row}-c${column}`,
        latitude: probeLat,
        longitude: probeLng,
        distanceFromCenter,
      });
    }
  }

  return coordinates
    .sort((left, right) => left.distanceFromCenter - right.distanceFromCenter)
    .slice(0, probeLimit)
    .map(({ key, latitude, longitude }) => ({ key, latitude, longitude }));
};

module.exports = { HELPER_CITY_GRID_SEARCH_OFFSET_MILES, HELPER_CITY_GRID_SEARCH_MAX_PROBES, MILES_PER_LATITUDE_DEGREE, toRadians, buildCityGridSearchCoordinates, normalizeLocationViewportBounds, getDistanceMiles, buildCityViewportGridSearchCoordinates };
